import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";

import { EmptyState } from "@/components/empty-state";
import { SectionHeading } from "@/components/section-heading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { WebhookActivityPanel } from "@/components/webhook-activity-panel";
import { buildCsv, downloadTextFile } from "@/lib/csv";
import { formatMoney } from "@/lib/money";
import {
  listFailedTipEvents,
  listWebhookFailures,
  reprocessWebhookFailure,
  resolveWebhookFailure,
} from "@/lib/payments/webhook-health.functions";

export const Route = createFileRoute("/_authenticated/admin/webhook-health")({
  head: () => ({
    meta: [
      { title: "Webhook health — Quibblies admin" },
      {
        name: "description",
        content:
          "Review payment callbacks that failed signature verification, inspect their payloads, and see every tip that ended failed or disputed.",
      },
      { property: "og:title", content: "Webhook health — Quibblies admin" },
      {
        property: "og:description",
        content: "Rejected payment callbacks and failed tip events, with full payload detail.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: WebhookHealthPage,
});

function Payload({ value }: { value: unknown }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="mt-2">
      <Button variant="ghost" size="sm" onClick={() => setOpen((prev) => !prev)}>
        {open ? "Hide payload" : "Show payload"}
      </Button>
      {open ? (
        <pre className="mt-2 max-h-72 overflow-auto rounded-xl bg-muted/60 p-3 text-xs text-muted-foreground">
          {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

function WebhookHealthPage() {
  const queryClient = useQueryClient();
  const loadFailures = useServerFn(listWebhookFailures);
  const loadFailedTips = useServerFn(listFailedTipEvents);
  const resolve = useServerFn(resolveWebhookFailure);
  const reprocess = useServerFn(reprocessWebhookFailure);

  const [onlyUnresolved, setOnlyUnresolved] = React.useState(true);
  const [notes, setNotes] = React.useState<Record<string, string>>({});

  const failures = useQuery({
    queryKey: ["webhook-failures", onlyUnresolved],
    queryFn: () => loadFailures({ data: { onlyUnresolved } }),
    retry: false,
  });

  const failedTips = useQuery({
    queryKey: ["failed-tip-events"],
    queryFn: () => loadFailedTips({ data: {} }),
    retry: false,
  });

  const reprocessMutation = useMutation({
    mutationFn: (input: { id: string; note?: string }) => reprocess({ data: input }),
    onSuccess: (result) => {
      toast.success(`Event reprocessed — ${result.outcome}`);
      void queryClient.invalidateQueries({ queryKey: ["webhook-failures"] });
      void queryClient.invalidateQueries({ queryKey: ["failed-tip-events"] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const resolveMutation = useMutation({
    mutationFn: (input: { id: string; note?: string }) => resolve({ data: input }),
    onSuccess: () => {
      toast.success("Marked as reviewed");
      void queryClient.invalidateQueries({ queryKey: ["webhook-failures"] });
    },
    onError: (error: Error) => toast.error("Could not update", { description: error.message }),
  });

  return (
    <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6">
      <SectionHeading
        title="Webhook health"
        description="Callbacks that could not be trusted, and tips whose payment ended badly. Nothing here has been applied to a tip."
      />

      <div className="mt-8">
        <WebhookActivityPanel />
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled={(failures.data ?? []).length === 0 && (failedTips.data ?? []).length === 0}
          onClick={() => {
            const rows: unknown[][] = [
              ...(failures.data ?? []).map((row) => [
                "rejected_callback",
                row.created_at,
                row.provider,
                row.environment ?? "",
                row.reason,
                row.signature_present ? "yes" : "no",
                row.resolved_at ?? "",
                row.resolution_note ?? "",
                row.raw_body ?? JSON.stringify(row.headers),
              ]),
              ...(failedTips.data ?? []).flatMap((tip) =>
                tip.events.length === 0
                  ? [[
                      "failed_tip",
                      tip.created_at,
                      tip.payment_provider ?? "",
                      "",
                      `${formatMoney(tip.amount_minor, tip.currency)} · ${tip.status}`,
                      "",
                      "",
                      tip.payment_reference ?? "",
                      "",
                    ]]
                  : tip.events.map((event) => [
                      "failed_tip",
                      event.receivedAt,
                      tip.payment_provider ?? "",
                      "",
                      `${formatMoney(tip.amount_minor, tip.currency)} · ${tip.status}`,
                      "",
                      `${event.eventType} (${event.result})`,
                      tip.payment_reference ?? "",
                      JSON.stringify(event.payload),
                    ]),
              ),
            ];
            downloadTextFile(
              `webhook-health-${new Date().toISOString().slice(0, 10)}.csv`,
              buildCsv(
                [
                  "record",
                  "occurred_at",
                  "provider",
                  "environment",
                  "detail",
                  "signature_present",
                  "event",
                  "resolution_or_reference",
                  "payload",
                ],
                rows,
              ),
            );
          }}
        >
          Export CSV
        </Button>
      </div>

      <div className="mt-4 flex items-center justify-between gap-4 rounded-2xl border border-border/60 bg-surface/50 p-4">
        <span className="text-sm text-foreground">Show only unreviewed rejections</span>
        <Switch checked={onlyUnresolved} onCheckedChange={setOnlyUnresolved} />
      </div>

      <h2 className="mt-8 mb-3 text-sm font-semibold tracking-wide text-muted-foreground uppercase">
        Rejected callbacks
      </h2>
      {failures.error ? (
        <p className="text-sm text-destructive">{failures.error.message}</p>
      ) : failures.isLoading ? (
        <Skeleton className="h-40 w-full rounded-2xl" />
      ) : (failures.data ?? []).length === 0 ? (
        <EmptyState
          title="No rejected callbacks"
          description="Every payment callback received so far carried a valid signature."
        />
      ) : (
        <ul className="space-y-3">
          {failures.data!.map((row) => (
            <li key={row.id} className="rounded-2xl border border-border/60 bg-surface/50 p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-foreground">{row.reason}</span>
                <span className="text-xs text-muted-foreground">
                  {new Date(row.created_at).toLocaleString()} · {row.environment ?? "unknown env"} ·{" "}
                  {row.signature_present ? "signature present" : "no signature header"}
                </span>
              </div>
              <Payload value={row.raw_body ?? row.headers} />
              {row.resolved_at ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  Reviewed {new Date(row.resolved_at).toLocaleString()}
                  {row.resolution_note ? ` — ${row.resolution_note}` : ""}
                </p>
              ) : (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Input
                    className="max-w-xs"
                    placeholder="Note (optional)"
                    value={notes[row.id] ?? ""}
                    onChange={(event) =>
                      setNotes((prev) => ({ ...prev, [row.id]: event.target.value }))
                    }
                  />
                  <Button
                    variant="outline"
                    disabled={resolveMutation.isPending}
                    onClick={() =>
                      resolveMutation.mutate({ id: row.id, note: notes[row.id] ?? "" })
                    }
                  >
                    Mark reviewed
                  </Button>
                  <Button
                    disabled={reprocessMutation.isPending || !row.raw_body}
                    onClick={() =>
                      reprocessMutation.mutate({ id: row.id, note: notes[row.id] ?? "" })
                    }
                    title={
                      row.raw_body
                        ? "Apply the stored payload again — safe to repeat"
                        : "No payload was stored for this callback"
                    }
                  >
                    Reprocess event
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <h2 className="mt-10 mb-3 text-sm font-semibold tracking-wide text-muted-foreground uppercase">
        Failed and disputed tips
      </h2>
      {failedTips.error ? (
        <p className="text-sm text-destructive">{failedTips.error.message}</p>
      ) : failedTips.isLoading ? (
        <Skeleton className="h-40 w-full rounded-2xl" />
      ) : (failedTips.data ?? []).length === 0 ? (
        <EmptyState
          title="No failed tips"
          description="No tip has ended in a failed or disputed state."
        />
      ) : (
        <ul className="space-y-3">
          {failedTips.data!.map((tip) => (
            <li key={tip.id} className="rounded-2xl border border-border/60 bg-surface/50 p-4">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <span className="text-sm font-medium text-foreground">
                  {formatMoney(tip.amount_minor, tip.currency)} · {tip.status}
                </span>
                <span className="text-xs text-muted-foreground">
                  {new Date(tip.created_at).toLocaleString()} · {tip.payment_reference ?? "no reference"}
                </span>
              </div>
              {tip.events.length === 0 ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  No provider event was ever recorded against this tip.
                </p>
              ) : (
                <ul className="mt-2 space-y-1">
                  {tip.events.map((event) => (
                    <li key={event.id} className="text-xs text-muted-foreground">
                      {new Date(event.receivedAt).toLocaleString()} — {event.eventType} (
                      {event.result})
                      <Payload value={event.payload} />
                    </li>
                  ))}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}

      <p className="mt-8 text-sm text-muted-foreground">
        To re-read a payment from Stripe and apply its verdict, use{" "}
        <Link to="/admin/tip-replay" className="underline">
          stuck tips &amp; replay
        </Link>
        . Replays require financial access, so they stay in that tool.
      </p>
    </div>
  );
}
